import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { 
  FiX, FiUser, FiMail, FiPhone, FiCalendar, FiGlobe, FiTarget, 
  FiMessageSquare, FiHash, FiShield, FiCheckCircle, FiInfo, FiCopy, FiCheck
} from 'react-icons/fi';
import { format } from 'date-fns';

export default function LeadDetails({ lead, isOpen, onClose }) {
  const [copiedField, setCopiedField] = useState(null);

  if (!lead) return null;

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const StatusBadge = ({ label, active }) => (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
      <SafeIcon icon={active ? FiCheckCircle : FiInfo} className="text-xs" />
      {label}: {active ? 'Sent' : 'N/A'}
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-navy-900/70 backdrop-blur-md"
          />
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }} 
            animate={{ opacity: 1, scale: 1, y: 0 }} 
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
          >
            {/* Header */}
            <div className="p-6 sm:p-8 border-b border-slate-100 flex justify-between items-start bg-slate-50/50">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-brand-blue flex items-center justify-center text-white shadow-xl shadow-brand-blue/20">
                  <SafeIcon icon={FiUser} className="text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 leading-tight">{lead.name || 'Lead Details'}</h2>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                    <p className="text-sm text-slate-500 flex items-center gap-1.5">
                      <SafeIcon icon={FiCalendar} />
                      {lead.created_at ? format(new Date(lead.created_at), 'PPPp') : 'Unknown Date'}
                    </p>
                    <p className="text-xs text-brand-blue font-bold flex items-center gap-1">
                      <SafeIcon icon={FiHash} />
                      ID: {lead.id?.slice(0, 8) || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="p-2.5 bg-white text-slate-400 rounded-full hover:bg-slate-100 hover:text-slate-600 transition-all shadow-sm border border-slate-100"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-grow overflow-y-auto p-6 sm:p-8 space-y-8 custom-scrollbar">
              {/* Notification Statuses */}
              <div className="flex flex-wrap gap-2">
                <StatusBadge label="Business Alert" active={lead.business_email_sent} />
                <StatusBadge label="Customer Ack" active={lead.customer_ack_sent} />
                <StatusBadge label="SMS Alert" active={lead.sms_sent} />
              </div>

              {/* Contact Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100 group relative">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-2">Email Address</span>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-slate-900 font-bold truncate">
                      <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm text-brand-blue">
                        <SafeIcon icon={FiMail} />
                      </div>
                      <span className="truncate">{lead.email || 'Not provided'}</span>
                    </div>
                    {lead.email && (
                      <button 
                        onClick={() => handleCopy(lead.email, 'email')}
                        className="ml-2 p-2 hover:bg-white rounded-lg transition-colors text-slate-400 hover:text-brand-blue"
                      >
                        <SafeIcon icon={copiedField === 'email' ? FiCheck : FiCopy} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100 group relative">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-2">Phone Number</span>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-slate-900 font-bold">
                      <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm text-brand-blue">
                        <SafeIcon icon={FiPhone} />
                      </div>
                      <span>{lead.phone || 'Not provided'}</span>
                    </div>
                    {lead.phone && (
                      <button 
                        onClick={() => handleCopy(lead.phone, 'phone')}
                        className="ml-2 p-2 hover:bg-white rounded-lg transition-colors text-slate-400 hover:text-brand-blue"
                      >
                        <SafeIcon icon={copiedField === 'phone' ? FiCheck : FiCopy} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Message Content */}
              <div>
                <div className="flex items-center justify-between mb-3 ml-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Submitted Message</span>
                  <SafeIcon icon={FiMessageSquare} className="text-slate-300" />
                </div>
                <div className="bg-navy-900 text-white rounded-[2rem] p-6 sm:p-8 relative overflow-hidden shadow-xl shadow-navy-900/10">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-brand-blue/10 rounded-full blur-3xl -mr-16 -mt-16"></div>
                  <p className="relative z-10 text-slate-200 leading-relaxed italic whitespace-pre-wrap font-medium">
                    "{lead.message || 'No message content provided.'}"
                  </p>
                </div>
              </div>

              {/* Technical Metadata */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 ml-1">
                  <SafeIcon icon={FiShield} className="text-brand-blue text-sm" />
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Technical Metadata</span>
                </div>
                <div className="bg-slate-50 rounded-[2rem] border border-slate-100 divide-y divide-slate-100 overflow-hidden">
                  <div className="p-4 px-6 flex flex-col sm:flex-row sm:items-center justify-between gap-2 hover:bg-slate-100/50 transition-colors">
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
                      <SafeIcon icon={FiGlobe} />
                      <span>Page URL</span>
                    </div>
                    <span className="text-xs font-medium text-slate-900 break-all sm:max-w-[300px] text-right">{lead.page_url || 'direct'}</span>
                  </div>
                  <div className="p-4 px-6 flex flex-col sm:flex-row sm:items-center justify-between gap-2 hover:bg-slate-100/50 transition-colors">
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
                      <SafeIcon icon={FiTarget} />
                      <span>UTM Source</span>
                    </div>
                    <span className="px-2 py-0.5 bg-brand-blue/5 text-brand-blue rounded text-[10px] font-bold uppercase">{lead.utm_source || 'organic'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 bg-slate-50/80 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Need technical detail?</p>
                <p className="text-xs text-slate-500">Contact BPAS Support with ID: {lead.id?.slice(0, 8)}</p>
              </div>
              <button 
                onClick={onClose}
                className="w-full sm:w-auto px-10 py-4 bg-navy-900 text-white font-black uppercase tracking-widest text-[10px] rounded-2xl hover:bg-navy-800 transition-all shadow-lg active:scale-95"
              >
                Close Portal View
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}